import React, { useMemo } from 'react';
import { FaLayerGroup, FaMapMarkerAlt, FaTag } from 'react-icons/fa';
import styles from '../VouchersWorkspacePage.module.css';

const QUEUE_ALL = { id: 'all', label: 'All', tone: 'neutral' };

const QUEUE_STATUS = [
  { id: 'attention', label: 'Needs attention', tone: 'warning' },
  { id: 'missing', label: 'Missing', tone: 'danger' },
  { id: 'partial', label: 'Partial', tone: 'partial' },
  { id: 'overpaid', label: 'Overpaid', tone: 'info' },
  { id: 'complete', label: 'Balanced', tone: 'success' },
  { id: 'closed', label: 'Closed', tone: 'muted' }
];

const QUEUE_ORDER = [QUEUE_ALL, ...QUEUE_STATUS];

const LABEL_SWATCH = {
  neutral: '#9aa3b2',
  brand: '#7c3aed',
  success: '#16a34a',
  info: '#2563eb',
  warning: '#d97706',
  danger: '#dc2626',
  accent: '#14b8a6'
};

export function presetCounts(rows) {
  const c = {
    attention: 0,
    missing: 0,
    partial: 0,
    overpaid: 0,
    complete: 0,
    closed: 0,
    all: rows.length
  };
  for (const row of rows) {
    const st = row.status;
    if (st === 'missing') c.missing += 1;
    if (st === 'partial') c.partial += 1;
    if (st === 'overpaid') c.overpaid += 1;
    if (st === 'complete') c.complete += 1;
    if (st === 'closed') c.closed += 1;
    if (st === 'missing' || st === 'partial' || st === 'overpaid') c.attention += 1;
  }
  return c;
}

function QueueOption({ item, selected, count, onSelect, dataAttr = 'preset' }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      data-preset={dataAttr === 'preset' ? item.id : undefined}
      data-loc={dataAttr === 'loc' ? item.id : undefined}
      title={item.label}
      tabIndex={selected ? 0 : -1}
      className={[styles.presetBtn, selected ? styles.presetBtnActive : ''].join(' ')}
      onClick={() => onSelect(item.id)}
    >
      <span className={[styles.presetDot, styles[`presetDot_${item.tone}`]].join(' ')} aria-hidden />
      <span className={styles.presetBtnLabel}>{item.label}</span>
      <span className={styles.presetBtnCount}>{count.toLocaleString()}</span>
    </button>
  );
}

export default function VouchersWorkspaceSidebar({
  settlementDates,
  selectedDate,
  onDateChange,
  preset,
  onPresetChange,
  rosterRows,
  sidebarLabels = [],
  labelFilterIds = [],
  labelOptionCounts = {},
  labelsLoadError = false,
  onToggleLabelFilter,
  onClearLabelFilter,
  locationFilter,
  locationCounts,
  onLocationChange,
  onResetFilters
}) {
  const counts = presetCounts(rosterRows);
  const selectedLabelSet = useMemo(() => new Set(labelFilterIds), [labelFilterIds]);

  const formatSettlementLabel = (d) => {
    if (!d?.settlement_date) return '—';
    const [datePart] = String(d.settlement_date).split('T');
    const [y, m, day] = datePart.split('-');
    const dt = new Date(Number(y), Number(m) - 1, Number(day));
    return Number.isNaN(dt.getTime())
      ? 'Invalid date'
      : dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <aside className={styles.sidebar}>
      <header className={styles.sidebarHeader}>
        <h3>Vouchers</h3>
        <button type="button" className={styles.sidebarReset} onClick={onResetFilters}>
          Reset
        </button>
      </header>

      <div className={styles.sidebarBlock}>
        <label className={styles.sidebarLabel}>
          <FaLayerGroup aria-hidden /> Settlement period
        </label>
        <select
          className={styles.sidebarSelect}
          value={selectedDate}
          onChange={(e) => onDateChange(e.target.value)}
        >
          {settlementDates.length === 0 ? (
            <option value="">No settlement periods</option>
          ) : (
            settlementDates.map((d) => (
              <option key={d.id} value={d.id}>
                {formatSettlementLabel(d)}
              </option>
            ))
          )}
        </select>
      </div>

      <div className={styles.sidebarBlock}>
        <span className={styles.sidebarLabel} id="voucher-queue-label">
          Queue
        </span>
        <div
          className={styles.presetGroup}
          role="radiogroup"
          aria-labelledby="voucher-queue-label"
          onKeyDown={(e) => {
            if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') {
              return;
            }
            e.preventDefault();
            const ids = QUEUE_ORDER.map((item) => item.id);
            const current = ids.indexOf(preset);
            let next = current < 0 ? 0 : current;
            if (e.key === 'Home') next = 0;
            else if (e.key === 'End') next = ids.length - 1;
            else if (e.key === 'ArrowDown') next = (next + 1) % ids.length;
            else next = (next - 1 + ids.length) % ids.length;
            const id = ids[next];
            onPresetChange(id);
            e.currentTarget.querySelector(`[data-preset="${id}"]`)?.focus();
          }}
        >
          <QueueOption
            item={QUEUE_ALL}
            selected={preset === QUEUE_ALL.id}
            count={counts.all ?? 0}
            onSelect={onPresetChange}
          />
          <div className={styles.presetGroupSplit}>
            <span>Status</span>
          </div>
          {QUEUE_STATUS.map((item) => (
            <QueueOption
              key={item.id}
              item={item}
              selected={preset === item.id}
              count={counts[item.id] ?? 0}
              onSelect={onPresetChange}
            />
          ))}
        </div>
      </div>

      <div className={styles.sidebarBlock}>
        <div className={styles.sidebarLabelRow}>
          <span className={styles.sidebarLabel}>
            <FaTag aria-hidden /> Labels
          </span>
          {labelFilterIds.length > 0 && onClearLabelFilter ? (
            <button type="button" className={styles.sidebarLabelClear} onClick={onClearLabelFilter}>
              Clear
            </button>
          ) : null}
        </div>
        <div className={styles.vouchLabelGroup}>
          {labelsLoadError ? (
            <p className={styles.vouchLabelEmpty}>
              Could not load labels. Check your connection, sign in again, or refresh the page.
            </p>
          ) : sidebarLabels.length === 0 ? (
            <p className={styles.vouchLabelEmpty}>
              No student labels yet. Add them from Directory → Manage labels.
            </p>
          ) : (
            <ul className={styles.vouchLabelList}>
              {sidebarLabels.map((l) => {
                const checked = selectedLabelSet.has(l.id);
                const c = (l.color || 'neutral').toLowerCase();
                const n = labelOptionCounts[l.id] ?? 0;
                return (
                  <li key={l.id}>
                    <label
                      className={[styles.vouchLabelItem, checked ? styles.vouchLabelItemOn : '']
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <input
                        type="checkbox"
                        className={styles.vouchLabelCheck}
                        checked={checked}
                        onChange={() => onToggleLabelFilter && onToggleLabelFilter(l.id)}
                      />
                      <span
                        className={styles.vouchLabelSwatch}
                        style={{ background: LABEL_SWATCH[c] || LABEL_SWATCH.neutral }}
                        aria-hidden
                      />
                      <span className={styles.vouchLabelName} title={l.name}>
                        {l.name}
                      </span>
                      <span className={styles.vouchLabelCount}>{n.toLocaleString()}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <div className={styles.sidebarBlock}>
        <span className={styles.sidebarLabel} id="voucher-location-label">
          <FaMapMarkerAlt aria-hidden /> Location
        </span>
        <div
          className={styles.presetGroup}
          role="radiogroup"
          aria-labelledby="voucher-location-label"
          onKeyDown={(e) => {
            if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') {
              return;
            }
            e.preventDefault();
            const buttons = [...e.currentTarget.querySelectorAll('[role="radio"]')];
            const current = buttons.findIndex((btn) => btn.getAttribute('aria-checked') === 'true');
            let next = current < 0 ? 0 : current;
            if (e.key === 'Home') next = 0;
            else if (e.key === 'End') next = buttons.length - 1;
            else if (e.key === 'ArrowDown') next = (next + 1) % buttons.length;
            else next = (next - 1 + buttons.length) % buttons.length;
            const id = buttons[next]?.dataset.loc;
            if (!id) return;
            onLocationChange(id);
            buttons[next].focus();
          }}
        >
          <QueueOption
            item={{ id: 'all', label: 'All', tone: 'neutral' }}
            selected={locationFilter === 'all'}
            count={Object.values(locationCounts).reduce((sum, n) => sum + n, 0)}
            onSelect={onLocationChange}
            dataAttr="loc"
          />
          <div className={styles.presetGroupSplit}>
            <span>Addresses</span>
          </div>
          <div className={styles.locGroupScroll}>
            {Object.entries(locationCounts)
              .sort(([a], [b]) => {
                if (a === 'No location') return 1;
                if (b === 'No location') return -1;
                return a.localeCompare(b);
              })
              .map(([loc, n]) => (
                <QueueOption
                  key={loc}
                  item={{
                    id: loc,
                    label: loc,
                    tone: loc === 'No location' ? 'muted' : 'info'
                  }}
                  selected={locationFilter === loc}
                  count={n}
                  onSelect={onLocationChange}
                  dataAttr="loc"
                />
              ))}
          </div>
        </div>
      </div>
    </aside>
  );
}
